import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input, Label, Select } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useDict, fill } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import { cn } from "@/lib/utils";

type AuthState = { error?: string; message?: string } | null;

const OTP_LENGTH = 6;

function Alert({ state }: { state: AuthState }) {
  if (!state) return null;
  if (state.error)
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {state.error}
      </p>
    );
  if (state.message)
    return (
      <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
        {state.message}
      </p>
    );
  return null;
}

function OtpBoxes({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] ?? "");

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  function focusAt(i: number) {
    refs.current[Math.max(0, Math.min(i, OTP_LENGTH - 1))]?.focus();
  }

  function handleChange(index: number, raw: string) {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      onChange(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    if (cleaned.length > 1) {
      const next = cleaned.slice(0, OTP_LENGTH);
      onChange(next);
      focusAt(next.length >= OTP_LENGTH ? OTP_LENGTH - 1 : next.length);
      return;
    }
    const next =
      index >= value.length
        ? (value + cleaned).slice(0, OTP_LENGTH)
        : (value.slice(0, index) + cleaned + value.slice(index + 1)).slice(
            0,
            OTP_LENGTH,
          );
    onChange(next);
    if (index < OTP_LENGTH - 1) focusAt(index + 1);
  }

  function handleKeyDown(
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index));
        focusAt(index - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    onChange(pasted);
    focusAt(Math.min(pasted.length, OTP_LENGTH) - 1);
  }

  return (
    <div className="flex justify-between gap-2" dir="ltr">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            "h-12 w-11 rounded-xl border border-border bg-card text-center text-lg font-semibold text-foreground",
            "focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200",
            "disabled:opacity-60 sm:h-14 sm:w-12",
          )}
        />
      ))}
    </div>
  );
}

type OtpStep = {
  challengeId: string;
  emailHint: string;
  emailed: boolean;
  devCode?: string;
};

export function SignInForm({
  onAuth,
}: {
  onAuth: () => void | Promise<void>;
}) {
  const t = useDict();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "";
  const [state, setState] = useState<AuthState>(null);
  const [pending, setPending] = useState(false);
  const [otp, setOtp] = useState<OtpStep | null>(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);

  async function onPasswordSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setState(null);
    const fd = new FormData(e.currentTarget);
    const staySignedIn = fd.get("remember") === "on";
    setRemember(staySignedIn);
    try {
      const res = await fetch("/api/auth/sign-in", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: String(fd.get("email") || ""),
          password: String(fd.get("password") || ""),
          redirect,
          remember: staySignedIn,
        }),
      });
      let data: {
        error?: string;
        code?: string;
        redirect?: string;
        requiresOtp?: boolean;
        challengeId?: string;
        emailHint?: string;
        emailed?: boolean;
        devCode?: string;
      } = {};
      try {
        data = (await res.json()) as typeof data;
      } catch {
        setState({
          error: res.ok
            ? t.errors.signinFailed
            : t.errors.networkError,
        });
        return;
      }
      if (!res.ok || data.error) {
        setState({
          error: resolveApiError(t, data, "signinFailed"),
        });
        return;
      }
      if (data.requiresOtp && data.challengeId) {
        setOtp({
          challengeId: data.challengeId,
          emailHint: data.emailHint || "",
          emailed: Boolean(data.emailed),
          devCode: data.devCode,
        });
        setCode(data.devCode || "");
        setState(
          data.devCode
            ? { message: fill(t.auth.otpDevHint, { code: data.devCode }) }
            : data.emailed
              ? { message: t.auth.otpSent }
              : { message: t.auth.otpEmailFailed },
        );
        return;
      }
      await onAuth();
      navigate(data.redirect || redirect || "/dashboard");
    } catch {
      setState({ error: t.errors.networkError });
    } finally {
      setPending(false);
    }
  }

  async function onVerifyOtp(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!otp) return;
    setPending(true);
    setState(null);
    try {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeId: otp.challengeId,
          code: code.trim(),
          redirect: redirect || undefined,
          remember,
        }),
      });
      const data = (await res.json()) as {
        error?: string;
        code?: string;
        redirect?: string;
      };
      if (!res.ok || data.error) {
        setState({ error: resolveApiError(t, data, "otpWrong") });
        return;
      }
      try {
        await onAuth();
      } catch {
        /* session cookie is set; still proceed */
      }
      navigate(data.redirect || redirect || "/dashboard");
    } catch {
      setState({ error: t.errors.networkError });
    } finally {
      setPending(false);
    }
  }

  async function onResend() {
    if (!otp) return;
    setPending(true);
    setState(null);
    try {
      const res = await fetch("/api/auth/resend-otp", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: otp.challengeId }),
      });
      const data = (await res.json()) as {
        error?: string;
        code?: string;
        challengeId?: string;
        emailHint?: string;
        emailed?: boolean;
        devCode?: string;
        retryAfterSec?: number;
      };
      if (!res.ok || data.error) {
        setState({
          error: resolveApiError(
            t,
            data,
            "otpResendFailed",
            data.retryAfterSec != null
              ? { seconds: data.retryAfterSec }
              : undefined,
          ),
        });
        return;
      }
      setOtp({
        challengeId: data.challengeId || otp.challengeId,
        emailHint: data.emailHint || otp.emailHint,
        emailed: Boolean(data.emailed),
        devCode: data.devCode,
      });
      setCode(data.devCode || "");
      setState({
        message: data.devCode
          ? fill(t.auth.otpDevHint, { code: data.devCode })
          : data.emailed
            ? t.auth.otpResent
            : t.auth.otpResendFailed,
      });
    } catch {
      setState({ error: t.auth.otpResendFailed });
    } finally {
      setPending(false);
    }
  }

  if (otp) {
    return (
      <Card className="animate-fade-in">
        <CardContent className="space-y-4 pt-6">
          <div>
            <h1 className="text-2xl font-bold">{t.auth.otpTitle}</h1>
            <p className="text-sm text-muted">
              {fill(t.auth.otpSubtitle, { email: otp.emailHint })}
            </p>
          </div>
          <form onSubmit={onVerifyOtp} className="space-y-4">
            <div>
              <Label>{t.auth.otpLabel}</Label>
              <OtpBoxes
                value={code}
                onChange={setCode}
                disabled={pending}
              />
            </div>
            <Alert state={state} />
            <Button
              type="submit"
              className="w-full"
              loading={pending}
              disabled={code.length !== OTP_LENGTH}
            >
              {t.auth.otpVerify}
            </Button>
          </form>
          <div className="flex flex-col items-center gap-2 text-sm">
            <button
              type="button"
              className="font-medium text-brand-600 hover:underline disabled:opacity-50"
              disabled={pending}
              onClick={onResend}
            >
              {t.auth.otpResend}
            </button>
            <button
              type="button"
              className="text-muted hover:text-foreground"
              disabled={pending}
              onClick={() => {
                setOtp(null);
                setCode("");
                setState(null);
              }}
            >
              {t.auth.otpBack}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-fade-in">
      <CardContent className="space-y-4 pt-6">
        <div>
          <h1 className="text-2xl font-bold">{t.auth.welcomeBack}</h1>
          <p className="text-sm text-muted">{t.auth.signInSubtitle}</p>
        </div>

        <form onSubmit={onPasswordSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">{t.auth.email}</Label>
            <Input id="email" name="email" type="email" required autoComplete="email" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="password">{t.auth.password}</Label>
              <Link
                to="/forgot-password"
                className="text-sm font-semibold text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
              >
                {t.auth.forgotPassword}
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input
              type="checkbox"
              name="remember"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            {t.auth.staySignedIn}
          </label>
          <Alert state={state} />
          <Button type="submit" className="w-full" loading={pending}>
            {t.common.signIn}
          </Button>
        </form>

        <p className="text-center text-sm text-muted">
          {t.auth.noAccount}{" "}
          <Link to="/sign-up" className="font-semibold text-brand-600">
            {t.common.signUp}
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

export function SignUpForm({
  onAuth,
}: {
  onAuth: () => void | Promise<void>;
}) {
  const t = useDict();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectParam = params.get("redirect");
  const defaultAgency = params.get("agency") === "1";
  const preferCandidate = params.get("as") === "candidate";
  const [state, setState] = useState<AuthState>(null);
  const [pending, setPending] = useState(false);
  const [isAgency, setIsAgency] = useState(defaultAgency);
  const [accountRole, setAccountRole] = useState<"manager" | "candidate">(
    preferCandidate ? "candidate" : "manager",
  );

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setState(null);
    const fd = new FormData(e.currentTarget);
    try {
      const res = await fetch("/api/auth/sign-up", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: String(fd.get("full_name") || ""),
          country: String(fd.get("country") || ""),
          company_name:
            accountRole === "manager"
              ? String(fd.get("company_name") || "")
              : "",
          email: String(fd.get("email") || ""),
          password: String(fd.get("password") || ""),
          company_type: isAgency ? "agency" : "client",
          role: accountRole,
        }),
      });
      const data = (await res.json()) as {
        error?: string;
        code?: string;
        redirect?: string;
      };
      if (!res.ok || data.error) {
        setState({ error: resolveApiError(t, data, "signupFailed") });
        return;
      }

      await onAuth();
      navigate(
        redirectParam ||
          data.redirect ||
          (accountRole === "candidate" ? "/careers" : "/dashboard"),
      );
    } catch {
      setState({ error: t.errors.networkError });
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="animate-fade-in">
      <CardContent className="space-y-4 pt-6">
        <div>
          <h1 className="text-2xl font-bold">{t.auth.createTitle}</h1>
          <p className="text-sm text-muted">{t.auth.createSubtitle}</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setAccountRole("manager")}
            className={cn(
              "rounded-xl border px-3 py-3 text-start transition",
              accountRole === "manager"
                ? "border-transparent bg-brand-600 text-white"
                : "border-border text-foreground hover:bg-surface",
            )}
          >
            <div className="text-sm font-semibold">{t.auth.roleHire}</div>
            <div
              className={cn(
                "mt-0.5 text-xs",
                accountRole === "manager" ? "text-white/85" : "text-muted",
              )}
            >
              {t.auth.roleHireDesc}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setAccountRole("candidate")}
            className={cn(
              "rounded-xl border px-3 py-3 text-start transition",
              accountRole === "candidate"
                ? "border-transparent bg-brand-600 text-white"
                : "border-border text-foreground hover:bg-surface",
            )}
          >
            <div className="text-sm font-semibold">{t.auth.roleSeek}</div>
            <div
              className={cn(
                "mt-0.5 text-xs",
                accountRole === "candidate" ? "text-white/85" : "text-muted",
              )}
            >
              {t.auth.roleSeekDesc}
            </div>
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="full_name">{t.auth.fullName}</Label>
            <Input id="full_name" name="full_name" required />
          </div>
          <div>
            <Label htmlFor="country">{t.auth.country}</Label>
            <Select id="country" name="country" required defaultValue="">
              <option value="" disabled>
                {t.auth.countryPh}
              </option>
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
          {accountRole === "manager" && (
            <>
              <div>
                <Label htmlFor="company_name">{t.auth.company}</Label>
                <Input id="company_name" name="company_name" required />
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground/80">
                <input
                  type="checkbox"
                  checked={isAgency}
                  onChange={(e) => setIsAgency(e.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                {t.auth.agencyToggle}
              </label>
            </>
          )}
          <div>
            <Label htmlFor="email">
              {accountRole === "manager" ? t.auth.workEmail : t.auth.email}
            </Label>
            <Input id="email" name="email" type="email" required />
          </div>
          <div>
            <Label htmlFor="password">{t.auth.password}</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <Alert state={state} />
          <Button type="submit" className="w-full" loading={pending}>
            {t.auth.createAccount}
          </Button>
        </form>
        <p className="text-center text-sm text-muted">
          {t.auth.alreadyHave}{" "}
          <Link to="/sign-in" className="font-semibold text-brand-600">
            {t.common.signIn}
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

export function ForgotPasswordForm() {
  const t = useDict();
  const [pending, setPending] = useState(false);
  const [state, setState] = useState<AuthState>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setState(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") || "").trim();
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json()) as {
        error?: string;
        code?: string;
        ok?: boolean;
        resetUrl?: string;
      };
      if (!res.ok || data.error) {
        setState({ error: resolveApiError(t, data, "requestFailed") });
        return;
      }
      setSent(true);
      setState({
        message: data.resetUrl
          ? fill(t.auth.forgotDevHint, { url: data.resetUrl })
          : t.auth.forgotSuccess,
      });
    } catch {
      setState({ error: t.errors.networkError });
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="animate-fade-in">
      <CardContent className="space-y-4 pt-6">
        <div>
          <h1 className="text-2xl font-bold">{t.auth.forgotTitle}</h1>
          <p className="text-sm text-muted">{t.auth.forgotSubtitle}</p>
        </div>

        {sent ? (
          <>
            <Alert state={state} />
            <p className="text-center text-sm text-muted">
              <Link to="/sign-in" className="font-semibold text-brand-600">
                {t.auth.forgotBack}
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t.auth.email}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
              />
            </div>
            <Alert state={state} />
            <Button type="submit" className="w-full" loading={pending}>
              {t.auth.forgotSubmit}
            </Button>
            <p className="text-center text-sm text-muted">
              <Link to="/sign-in" className="font-semibold text-brand-600">
                {t.auth.forgotBack}
              </Link>
            </p>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

export function ResetPasswordForm() {
  const t = useDict();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token")?.trim() || "";
  const [pending, setPending] = useState(false);
  const [state, setState] = useState<AuthState>(
    token ? null : { error: t.auth.resetMissingToken },
  );
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) {
      setState({ error: t.auth.resetMissingToken });
      return;
    }
    setPending(true);
    setState(null);
    const fd = new FormData(e.currentTarget);
    const password = String(fd.get("password") || "");
    const confirm = String(fd.get("confirm") || "");
    if (password.length < 8) {
      setState({ error: t.auth.resetTooShort });
      setPending(false);
      return;
    }
    if (password !== confirm) {
      setState({ error: t.auth.resetMismatch });
      setPending(false);
      return;
    }
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = (await res.json()) as {
        error?: string;
        code?: string;
        ok?: boolean;
      };
      if (!res.ok || data.error) {
        setState({ error: resolveApiError(t, data, "resetInvalid") });
        return;
      }
      setDone(true);
      setState({ message: t.auth.resetSuccess });
      window.setTimeout(() => navigate("/sign-in"), 1500);
    } catch {
      setState({ error: t.errors.networkError });
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="animate-fade-in">
      <CardContent className="space-y-4 pt-6">
        <div>
          <h1 className="text-2xl font-bold">{t.auth.resetTitle}</h1>
          <p className="text-sm text-muted">{t.auth.resetSubtitle}</p>
        </div>

        {done ? (
          <>
            <Alert state={state} />
            <p className="text-center text-sm text-muted">
              <Link to="/sign-in" className="font-semibold text-brand-600">
                {t.common.signIn}
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">{t.auth.resetPassword}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                disabled={!token}
              />
            </div>
            <div>
              <Label htmlFor="confirm">{t.auth.resetConfirm}</Label>
              <Input
                id="confirm"
                name="confirm"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                disabled={!token}
              />
            </div>
            <Alert state={state} />
            <Button
              type="submit"
              className="w-full"
              loading={pending}
              disabled={!token}
            >
              {t.auth.resetSubmit}
            </Button>
            <p className="text-center text-sm text-muted">
              <Link to="/forgot-password" className="font-semibold text-brand-600">
                {t.auth.forgotPassword}
              </Link>
            </p>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
